<script lang="ts">
  import { Chip, type FacetValue } from "@otto-ec/heureka-components/facet-value";
  import { type FacetValueLabelOptions, Text } from "@otto-ec/heureka-components/facet-value-label";
  import { OverflowBox } from "@otto-ec/heureka-components/facet-value-group";
  import { dereference, updateLabel } from "../tracking/FeatureTracking";
  import { getElementById } from "../util/Utils";
  import { selectedCategoryPathValues } from "../categorypath/categoryPathStore";
  import { heureka } from "@otto-ec/heureka";
  import { events } from "../events";

  const defaultFacetValueLabelOptions: Partial<FacetValueLabelOptions> = {
    size: "75",
    showCount: false,
  };

  interface Props {
    values?: FacetValue[];
    rule?: string;
  }

  const { values = [], rule }: Props = $props();
  const initialSelectedValues = $derived(values.filter((value) => value.checked));
  const initialSelectedIds = $derived(initialSelectedValues.map((value) => value.referencedFacetValueId));
  const selectedCategories: FacetValue[] = $derived(
    Array.from($selectedCategoryPathValues)
      .filter((value) => !initialSelectedIds.includes(value.id))
      .map((value) => {
        return {
          checked: value.checked,
          title: value.title,
          referencedFacetValueId: value.ref || value.id,
        };
      }),
  );
  const selectedValues = $derived(initialSelectedValues.concat(selectedCategories));
  const titles = $derived(selectedValues.map((value) => value.title));
  const hasValues = $derived(selectedValues?.length > 0);

  function onchange(event: Event) {
    if (event.target instanceof HTMLElement) {
      const referenced = dereference(event.target);
      const filterForm = referenced.closest("form");
      if (filterForm) {
        if (event.target instanceof HTMLInputElement) {
          filterForm.dataset.tsFeatureFilterMethod = "x_single_local";
        }
      }
    }
    heureka.filterSection_change.emit({ target: event.target });
  }

  $effect(() => {
    selectedValues.forEach((value, index) => {
      if (value.referencedFacetValueId) {
        const referenced = getElementById(value.referencedFacetValueId);
        if (referenced) {
          updateLabel(referenced, "san_FacetValueTagPosition", (index + 1).toString());
        }
      }
    });
  });

  $effect(() => {
    if (values.length > 0) {
      events.facetValueTagsLoaded(rule, titles);
    }
  });
</script>

{#snippet chip(facetValue: FacetValue)}
  <!-- Remove checked attribute - render gray -->
  <Chip
    class={["hc_facetValueTag", { js_clearInput: facetValue.checked }]}
    {...facetValue}
    checked={facetValue.checked}
    showRemoveIcon={true}
  >
    <Text {...facetValue} {...defaultFacetValueLabelOptions} />
  </Chip>
{/snippet}

{#if hasValues}
  <div class="hc_facetValueTags">
    <div class="hc_facetValueTags__values" {onchange}>
      <OverflowBox values={selectedValues} item={chip}></OverflowBox>
    </div>
  </div>
{/if}

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .hc_facetValueTags {
    &__values {
      .hc_facetValue__icon {
        width: tokens.$oc-semantic-spacing-75;
        height: tokens.$oc-semantic-spacing-75;
        margin-left: tokens.$oc-semantic-spacing-25;
      }
    }
  }
</style>
